<template>
	<transition name="bottom-menu-entry-animation" mode="out-in">
		<div class="bottom-mobile-menu-wrapper">
			<navigation-primary-tab
			v-for="menu in bottomMenu"
			:key="`${menu.id}-bottom-menu`"
			:params="menu"
			:selected="menu.id === selectedMenu"
			@clicked="emit('clicked', menu)"
			@updateMenu="updateMenuEntries"/>
		</div>
	</transition>
</template>

<script setup>
import NavigationPrimaryTab from '@/components/generic/navigation/primary/Tab'
/* Vue */
import { computed, watch } from 'vue'
/* Aux */
import { menuIconThirdLevelMapper, menuIconMapper } from '@/assets/scripts/mappers/menu'
import { getMenuItemData } from '@/assets/scripts/parsers/menu'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const { getMenu, getSubMenu } = mapGetters(moduleMapper.MENU)

/* Emmitters */
const emit = defineEmits(['clicked'])

/** props received
@selectedMenu { String, Number } -prop with the current selected Menu
*/
const props = defineProps({
    selectedMenu: [String, Number]
})

const homeEntry = {
	id: 'home',
	text: t('menu.mobileMenu.home'),
	icon: 'dashboard',
	label: 'home'
}

const menuEntry = {
	id: 'menu',
	text: t('menu.mobileMenu.menu'),
	icon: 'menu',
	label: 'menu'
}

const bottomMenu = computed(() => {
	if (!getSubMenu.value.length) return [homeEntry, menuEntry]

	const bottomEntries = getSubMenu.value.filter(obj => !obj.fromBullet).map(obj => {
		/* First finding if this entry exists in our menu */
		const menuParentName = obj.menuItem.name
		const bulletsForEntry = getSubMenu.value.filter(item => item.fromBullet && item.menuItemTopId === obj.menuItem.id)

		const subitems = bulletsForEntry.reduce((acc, subitem) => {
			if (getMenuItemData(subitem.menuItem.id)) {
				acc.push({
					...subitem.menuItem,
					description: getMenuItemData(subitem.menuItem.id).additionalData?.description,
					icon: menuIconThirdLevelMapper(subitem.menuItem.label)
				})
			}

			return acc
		}, [])

		return {
			id: obj.menuItem.id,
			text: obj.menuItem.name,
			icon: menuIconMapper(obj.menuItem.label),
			label: obj.menuItem.label,
			link: obj.menuItem.link,
			menuParentName: menuParentName,
			subitems: subitems
		}
	})
	return [homeEntry, ...bottomEntries, menuEntry]
})

watch(() => getMenu.value, (newVal, oldVal) => {
	if ((props.selectedMenu !== 'home' || props.selectedMenu !== 'menu')) {
		emit('clicked', bottomMenu.value.find(obj => obj.id === props.selectedMenu))
	}
})
</script>

<style lang="scss" scoped>
.bottom-mobile-menu-wrapper {
	width: 100%;
	min-height: 56px;

	padding-bottom: constant(safe-area-inset-bottom); /* iOS 11.0 */
	padding-bottom: env(safe-area-inset-bottom); /* iOS 11.2 */

	position: absolute;
	bottom: 0;
	z-index: 1;

	display: flex;
	justify-content: space-between;

	background-color: var(--secondary-light-3);
	overflow: hidden;

	box-shadow: 4px 4px 16px 0 rgba(var(--primary-variant-3-rgb), 0.06);

	transition: transform 0.4s linear;
    transform: translateY(0);

    &.bottom-menu-entry-animation-enter-active,
    &.bottom-menu-entry-animation-leave-active {
        transform:translateY(0);
    }

    &.bottom-menu-entry-animation-enter-from,
    &.bottom-menu-entry-animation-leave-to {
        transform:translateY(110%);
    }
}

.bullet-menu-wrapper {
	width: 100%;
	height: 100%;
    padding-bottom: 16px;

	position: absolute;
	bottom: 56px;

	display: flex;
    flex-direction: column-reverse;
    align-items: flex-end;
    justify-content: flex-start;
	gap: 16px;

	background: rgb(255,255,255);
	background: linear-gradient(0deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%);

	transition: all 0.4s linear;

	&.bullet-menu-entry-animation-enter-active,
	&.bullet-menu-entry-animation-leave-active {
		height: 100%;
	}

	&.bullet-menu-entry-animation-enter-from,
	&.bullet-menu-entry-animation-leave-to {
		height: 0;
	}
}
</style>
